import { useCallback, useEffect, useState } from 'react';

import { useIntl } from 'react-intl-next';
import invariant from 'tiny-invariant';

import type { Instruction } from '@atlaskit/navigation-system/side-nav-items/drag-and-drop/hitbox';
import { triggerPostMoveFlash } from '@atlaskit/pragmatic-drag-and-drop-flourish/trigger-post-move-flash';
import * as liveRegion from '@atlaskit/pragmatic-drag-and-drop-live-region';
import { reorder } from '@atlaskit/pragmatic-drag-and-drop/reorder';
import type { ElementRegistry } from '@atlassian/navigation-drag-and-drop';

import { useAnalyticsEvents } from '../../common/analytics/generated/use-analytics-events';
import type { UserMenuDataShapes } from '../../common/types';
import { useNavAnalytics } from '../../common/utils/use-nav-analytics';

import { messages } from './messages';

export interface DragAndDropAction {
	draggedMenuItemId: string;
	draggedMenuItemLabel?: string;
	droppedOnMenuItemId?: string;
	instruction: Instruction;
	trigger: 'pointer' | 'keyboard';
	type: 'item-reorder' | 'item-hide' | 'item-unhide' | 'item-visible';
}

type DragAndDropActionExtended =
	| ReorderDragAndDropAction
	| HideDragAndDropAction
	| UnhideDragAndDropAction
	| VisibleDragAndDropAction;

interface ReorderDragAndDropAction extends DragAndDropAction {
	finishIndex: number;
	startIndex: number;
	type: 'item-reorder';
}

interface HideDragAndDropAction extends DragAndDropAction {
	startIndex: number;
	type: 'item-hide';
	// finish is the More menu
}

interface UnhideDragAndDropAction extends DragAndDropAction {
	finishIndex: number;
	type: 'item-unhide';
	// start is the More menu
}

interface VisibleDragAndDropAction extends DragAndDropAction {
	type: 'item-visible';
}

export function useDragAndDropSave(
	userMenuDataShapes: UserMenuDataShapes,
	elementRegistry: ElementRegistry,
) {
	/*
	Get current order of items and shortcuts
	Apply reorder or hide operation
	Save the updated data using onSave
	*/
	const intl = useIntl();
	const [lastDragAndDropAction, setLastDragAndDropAction] =
		useState<DragAndDropActionExtended | null>(null);

	const { fireEvent } = useAnalyticsEvents();
	const { fireDraggedSideNavItemEvent } = useNavAnalytics();

	// Clean up live region when unmounted
	useEffect(() => {
		return () => liveRegion.cleanup();
	}, []);

	const dragAndDropSave = useCallback(
		async (action: DragAndDropAction) => {
			// item reordered into visible menu
			if (isItemReorderAction(action)) {
				invariant(action.droppedOnMenuItemId, 'item-reorder action must have droppedOnMenuItemId');

				const startIndex = userMenuDataShapes.userMenuData.findIndex(
					(datum) => datum.menuId === action.draggedMenuItemId,
				);
				const itemsAndShortcutsIdsWithoutDraggedItem = userMenuDataShapes.userMenuData
					.map((datum) => datum.menuId)
					.filter((id) => id !== action.draggedMenuItemId);
				const dropTargetIndex = itemsAndShortcutsIdsWithoutDraggedItem.indexOf(
					action.droppedOnMenuItemId,
				);

				if (startIndex === -1) {
					fireEvent('operational.sideNav.actionFailed', {
						operation: 'dragAndDropSave',
						reason: `startIndex not found for ${action.draggedMenuItemId}`,
					});
					return;
				}

				if (dropTargetIndex === -1) {
					fireEvent('operational.sideNav.actionFailed', {
						operation: 'dragAndDropSave',
						reason: `dropTargetIndex not found for ${action.draggedMenuItemId}`,
					});
					return;
				}

				const finishIndex =
					action.instruction.operation === 'reorder-after' ? dropTargetIndex + 1 : dropTargetIndex;

				const visibleStartIndex = userMenuDataShapes.visibleMenuIds.indexOf(
					action.draggedMenuItemId,
				);
				const visibleDropTargetIndex = userMenuDataShapes.visibleMenuIds
					.filter((id) => id !== action.draggedMenuItemId)
					.indexOf(action.droppedOnMenuItemId);

				if (visibleStartIndex === -1) {
					fireEvent('operational.sideNav.actionFailed', {
						operation: 'dragAndDropSave',
						reason: `visibleStartIndex not found for ${action.draggedMenuItemId}`,
					});
					return;
				}

				if (visibleDropTargetIndex === -1) {
					fireEvent('operational.sideNav.actionFailed', {
						operation: 'dragAndDropSave',
						reason: `visibleDropTargetIndex not found for ${action.draggedMenuItemId}`,
					});
					return;
				}

				const visibleFinishIndex =
					action.instruction.operation === 'reorder-after'
						? visibleDropTargetIndex + 1
						: visibleDropTargetIndex;

				const reordered = reorder({
					list: userMenuDataShapes.userMenuData,
					startIndex,
					finishIndex,
				});

				userMenuDataShapes.setUserMenuData(reordered);

				setLastDragAndDropAction({
					...action,
					startIndex: visibleStartIndex,
					finishIndex: visibleFinishIndex,
				});
				return;
			}

			// item is dragged into hide button
			if (isItemHideAction(action)) {
				const dataWithHiddenItem = userMenuDataShapes.userMenuData.map((datum) => {
					if (datum.menuId === action.draggedMenuItemId) {
						return {
							...datum,
							visible: false,
						};
					}
					return datum;
				});

				userMenuDataShapes.setUserMenuData(dataWithHiddenItem);

				setLastDragAndDropAction(action);

				return;
			}

			// hidden item dragged into visible button
			if (isItemVisibleAction(action)) {
				const dataWithVisibleItem = userMenuDataShapes.userMenuData.map((datum) => {
					if (datum.menuId === action.draggedMenuItemId) {
						return {
							...datum,
							visible: true,
						};
					}
					return datum;
				});

				userMenuDataShapes.setUserMenuData(dataWithVisibleItem);

				setLastDragAndDropAction(action);

				return;
			}

			// hidden item dragged into visible menu
			if (isItemUnhideAction(action)) {
				invariant(action.droppedOnMenuItemId, 'item-unhide action must have droppedOnMenuItemId');
				const userMenuDataWithUnhiddenItem = userMenuDataShapes.userMenuData.map((data) => {
					if (data.menuId === action.draggedMenuItemId) {
						return {
							...data,
							visible: true,
						};
					}
					return data;
				});
				const startIndex = userMenuDataWithUnhiddenItem.findIndex(
					(datum) => datum.menuId === action.draggedMenuItemId,
				);
				const itemsAndShortcutsIdsWithoutDraggedItem = userMenuDataWithUnhiddenItem
					.map((datum) => datum.menuId)
					.filter((id) => id !== action.draggedMenuItemId);
				const dropTargetIndex = itemsAndShortcutsIdsWithoutDraggedItem.indexOf(
					action.droppedOnMenuItemId,
				);

				if (startIndex === -1) {
					fireEvent('operational.sideNav.actionFailed', {
						operation: 'dragAndDropSave',
						reason: `startIndex not found for ${action.draggedMenuItemId}`,
					});
					return;
				}

				if (dropTargetIndex === -1) {
					fireEvent('operational.sideNav.actionFailed', {
						operation: 'dragAndDropSave',
						reason: `dropTargetIndex not found for ${action.draggedMenuItemId}`,
					});
					return;
				}

				const finishIndex =
					action.instruction.operation === 'reorder-after' ? dropTargetIndex + 1 : dropTargetIndex;

				const visibleDropTargetIndex = userMenuDataShapes.visibleMenuIds
					.filter((id) => id !== action.draggedMenuItemId)
					.indexOf(action.droppedOnMenuItemId);

				if (visibleDropTargetIndex === -1) {
					fireEvent('operational.sideNav.actionFailed', {
						operation: 'dragAndDropSave',
						reason: `visibleDropTargetIndex not found for ${action.draggedMenuItemId}`,
					});
					return;
				}

				const visibleFinishIndex =
					action.instruction.operation === 'reorder-after'
						? visibleDropTargetIndex + 1
						: visibleDropTargetIndex;

				const reordered = reorder({
					list: userMenuDataWithUnhiddenItem,
					startIndex,
					finishIndex,
				});

				userMenuDataShapes.setUserMenuData(reordered);

				setLastDragAndDropAction({
					...action,
					finishIndex: visibleFinishIndex,
				});
				return;
			}
		},
		[fireEvent, userMenuDataShapes],
	);

	// After action has been performed, announce & flash the item
	useEffect(() => {
		if (!lastDragAndDropAction) {
			return;
		}
		if (isItemReorderAction(lastDragAndDropAction)) {
			const label =
				lastDragAndDropAction.draggedMenuItemLabel || lastDragAndDropAction.draggedMenuItemId;

			const element = elementRegistry.getL1ItemElement(lastDragAndDropAction.draggedMenuItemId);
			if (element) {
				// Flash the moved item
				triggerPostMoveFlash(element);

				// Focus the moved item if move was triggered by keyboard
				if (lastDragAndDropAction.trigger === 'keyboard') {
					// Per Atlaskit team, .focus() seems to be more reliable in the next task when used with VoiceOver
					setTimeout(() => element.focus());
				}

				// Announce the move for assistive technology users
				liveRegion.announce(
					intl.formatMessage(messages.a11yAnnouncementItemReordered, {
						label,
						startIndex: lastDragAndDropAction.startIndex + 1,
						finishIndex: lastDragAndDropAction.finishIndex + 1,
					}),
				);

				// Fire analytics event
				fireDraggedSideNavItemEvent(lastDragAndDropAction.draggedMenuItemId, false);
			}
			return;
		}

		if (isItemHideAction(lastDragAndDropAction)) {
			const label =
				lastDragAndDropAction.draggedMenuItemLabel || lastDragAndDropAction.draggedMenuItemId;

			const moreMenuElement = elementRegistry.getMoreMenuElement();
			if (moreMenuElement) {
				// Flash the More menu
				triggerPostMoveFlash(moreMenuElement);

				// Focus the More menu if move was triggered by keyboard
				if (lastDragAndDropAction.trigger === 'keyboard') {
					// Per Atlaskit team, .focus() seems to be more reliable in the next task when used with VoiceOver
					setTimeout(() => moreMenuElement.focus());
				}

				// Announce the hide for assistive technology users
				liveRegion.announce(
					intl.formatMessage(messages.a11yAnnouncementItemHidden, {
						label,
					}),
				);

				// Fire analytics event
				fireDraggedSideNavItemEvent(lastDragAndDropAction.draggedMenuItemId, true);
			}
		}

		if (isItemUnhideAction(lastDragAndDropAction)) {
			const label =
				lastDragAndDropAction.draggedMenuItemLabel || lastDragAndDropAction.draggedMenuItemId;

			const element = elementRegistry.getL1ItemElement(lastDragAndDropAction.draggedMenuItemId);
			if (element) {
				// Flash the moved item
				triggerPostMoveFlash(element);

				// Focus the moved item if move was triggered by keyboard
				if (lastDragAndDropAction.trigger === 'keyboard') {
					// Per Atlaskit team, .focus() seems to be more reliable in the next task when used with VoiceOver
					setTimeout(() => element.focus());
				}

				// Announce the move for assistive technology users
				liveRegion.announce(
					intl.formatMessage(messages.a11yAnnouncementItemUnhidden, {
						label,
						finishIndex: lastDragAndDropAction.finishIndex + 1,
					}),
				);

				// Fire analytics event
				fireDraggedSideNavItemEvent(lastDragAndDropAction.draggedMenuItemId, false);
			}
			return;
		}

		if (isItemVisibleAction(lastDragAndDropAction)) {
			const label =
				lastDragAndDropAction.draggedMenuItemLabel || lastDragAndDropAction.draggedMenuItemId;

			const moreMenuElement = elementRegistry.getMoreMenuElement();
			if (moreMenuElement) {
				// Announce the hide for assistive technology users
				liveRegion.announce(
					intl.formatMessage(messages.a11yAnnouncementItemVisible, {
						label,
					}),
				);

				// Fire analytics event
				fireDraggedSideNavItemEvent(lastDragAndDropAction.draggedMenuItemId, false);
			}
		}
		// Intentional omission of `fireDraggedSideNavItemEvent` from dependencies to avoid firing twice
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [elementRegistry, intl, lastDragAndDropAction]);

	return {
		dragAndDropSave,
	};
}

function isItemReorderAction(action: DragAndDropAction): action is ReorderDragAndDropAction {
	return action.type === 'item-reorder';
}

function isItemHideAction(action: DragAndDropAction): action is HideDragAndDropAction {
	return action.type === 'item-hide';
}

function isItemUnhideAction(action: DragAndDropAction): action is UnhideDragAndDropAction {
	return action.type === 'item-unhide';
}

function isItemVisibleAction(action: DragAndDropAction): action is VisibleDragAndDropAction {
	return action.type === 'item-visible';
}
